import { Controller } from "@hotwired/stimulus";
import { Device } from "@twilio/voice-sdk";

export default class extends Controller {
  static values = {
    token: String,
  };

  connect() {
    // Check if this is actually a Twilio call
    const isTelnyx = this.element.getAttribute("data-controller").includes("telnyx-client");
    if (isTelnyx) {
      console.warn("Twilio: Skipping initialization on Telnyx call.");
      return;
    }

    // Restore active call reference from window (survives Turbo navigation)
    this.currentCall = window.currentTwilioCall || null;
    this.isMuted = false;
    this.selectedSpeakerId = null;
    this.selectedMicId = null;

    if (!this.hasTokenValue || !this.tokenValue) {
      console.warn("Twilio token missing – skipping client initialization.");
      return;
    }

    // Reuse existing device if available (for Turbo navigation)
    if (window.twilioDevice && window.twilioDevice.state !== "destroyed") {
      this.device = window.twilioDevice;
      this.setupAudioUnlock();
      this.setupDeviceChangeListener();
      return;
    }

    try {
      // Ensure Telnyx is fully disabled to prevent browser resource conflicts
      this.ensureTelnyxDisabled();
      this.initializeDevice();
      this.setupAudioUnlock();
      this.setupDeviceChangeListener();
    } catch (error) {
      console.error("Failed to initialize Twilio Device:", error);
    }
  }

  disconnect() {
    // Don't destroy the device on Turbo navigation
    // Only clean up event listeners
    if (this.audioUnlockHandler) {
      document.removeEventListener("click", this.audioUnlockHandler);
      document.removeEventListener("keydown", this.audioUnlockHandler);
      this.audioUnlockHandler = null;
    }

    // Clean up device change listener
    if (this.deviceChangeHandler) {
      navigator.mediaDevices.removeEventListener("devicechange", this.deviceChangeHandler);
      this.deviceChangeHandler = null;
    }

    // Keep device alive for Turbo navigation
    // Device will be cleaned up on actual page unload
  }

  // Explicitly destroy Telnyx device if it exists
  ensureTelnyxDisabled() {
    if (window.telnyxDevice) {
      console.warn("Twilio: Disconnecting from Telnyx to prevent conflicts...");
      try {
        if (typeof window.telnyxDevice.disconnect === "function") {
          window.telnyxDevice.disconnect();
        }
        window.telnyxDevice = null;
      } catch (e) {
        console.warn("Twilio: Error disconnecting Telnyx device:", e);
      }
    }
  }

  async initializeDevice() {
    try {
      // Get available audio input and output devices
      let selectedMicId = null;
      let selectedSpeakerId = null;

      try {
        // Request permission with proper audio constraints for headsets/AirPods
        // These constraints help with Bluetooth audio quality and prevent echo
        await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          }
        });

        const devices = await navigator.mediaDevices.enumerateDevices();
        const microphones = devices.filter(d => d.kind === "audioinput");
        const speakers = devices.filter(d => d.kind === "audiooutput");

        // Prefer "default" device as it usually respects system settings (e.g. selected AirPods)
        const defaultMic = microphones.find(m => m.deviceId === "default");
        const defaultSpeaker = speakers.find(s => s.deviceId === "default");

        if (defaultMic) {
          selectedMicId = defaultMic.deviceId;
        } else if (microphones.length > 0) {
          selectedMicId = microphones[0].deviceId;
        }

        if (defaultSpeaker) {
          selectedSpeakerId = defaultSpeaker.deviceId;
        } else if (speakers.length > 0) {
          selectedSpeakerId = speakers[0].deviceId;
        }

        // Store for later use (device changes, etc.)
        this.selectedMicId = selectedMicId;
        this.selectedSpeakerId = selectedSpeakerId;

        console.warn("Audio devices detected:", {
          microphones: microphones.map(m => ({ id: m.deviceId, label: m.label })),
          speakers: speakers.map(s => ({ id: s.deviceId, label: s.label })),
          selectedMic: selectedMicId,
          selectedSpeaker: selectedSpeakerId
        });
      } catch (e) {
        console.warn("Could not enumerate devices, falling back to browser default:", e);
      }

      const deviceOptions = {
        debug: true,
        logLevel: "debug",
        codecPreferences: ["opus", "pcmu"],
      };

      // Add audio constraints with echo cancellation, noise suppression for headsets
      // These are critical for AirPods and Bluetooth headsets
      deviceOptions.audioConstraints = selectedMicId ? {
        deviceId: { exact: selectedMicId },
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      } : {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      };

      this.device = new Device(this.tokenValue, deviceOptions);

      this.device.on("registered", async () => {
        this.resumeAudioContext();

        // Set the output (speaker) device - critical for AirPods/headsets
        // Without this, audio may play through laptop speakers instead of headset
        await this.setOutputDevice(selectedSpeakerId);
      });

      this.device.on("error", (error) => {
        this.handleDeviceError(error);
      });

      this.device.on("unregistered", () => {
        console.warn("Twilio Client went offline");
      });

      this.device.on("incoming", (call) => {
        if (Notification.permission === "granted") {
          new Notification("Incoming Call", {
            body: `From: ${call.parameters.From}`,
            icon: "/favicon.ico",
          });
        }
      });

      // Register the device
      this.device.register();

      window.twilioDevice = this.device;
    } catch (error) {
      console.error("Failed to initialize Twilio Device:", error);
    }
  }

  setupAudioUnlock() {
    if (this.audioUnlockHandler) return;

    this.audioUnlockHandler = () => {
      this.resumeAudioContext();
    };

    document.addEventListener("click", this.audioUnlockHandler, { passive: true });
    document.addEventListener("keydown", this.audioUnlockHandler, { passive: true });
  }

  resumeAudioContext() {
    try {
      const audioContext =
        this.device?.audio?.audioContext ||
        window.Twilio?.Device?.audioContext ||
        this.device?._audio?.audioContext;

      if (audioContext && audioContext.state === "suspended") {
        audioContext.resume();
      }
    } catch (error) {
      console.warn("Unable to resume Twilio audio context:", error);
    }
  }

  // Set the output (speaker) device for the Twilio call
  // This is critical for AirPods and Bluetooth headsets to work correctly
  async setOutputDevice(speakerId) {
    if (!speakerId || !this.device?.audio) {
      return;
    }

    try {
      // Twilio Voice SDK method to set speaker device
      if (this.device.audio.speakerDevices && typeof this.device.audio.speakerDevices.set === "function") {
        await this.device.audio.speakerDevices.set(speakerId);
        console.warn("Speaker device set to:", speakerId);
      }

      // Also set ringtone device to match
      if (this.device.audio.ringtoneDevices && typeof this.device.audio.ringtoneDevices.set === "function") {
        await this.device.audio.ringtoneDevices.set(speakerId);
        console.warn("Ringtone device set to:", speakerId);
      }

      this.selectedSpeakerId = speakerId;
    } catch (error) {
      console.warn("Could not set speaker device:", error);
    }
  }

  // Listen for audio device changes (Bluetooth connect/disconnect)
  // This handles AirPods being connected/disconnected during a session
  setupDeviceChangeListener() {
    if (this.deviceChangeHandler) return;

    this.deviceChangeHandler = async () => {
      console.warn("Audio device change detected");
      await this.handleDeviceChange();
    };

    navigator.mediaDevices.addEventListener("devicechange", this.deviceChangeHandler);
  }

  // Handle audio device changes (e.g., AirPods connected/disconnected)
  async handleDeviceChange() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const microphones = devices.filter(d => d.kind === "audioinput");
      const speakers = devices.filter(d => d.kind === "audiooutput");

      // Find the new default devices
      const defaultMic = microphones.find(m => m.deviceId === "default") || microphones[0];
      const defaultSpeaker = speakers.find(s => s.deviceId === "default") || speakers[0];

      console.warn("Device change - new defaults:", {
        mic: defaultMic?.label,
        speaker: defaultSpeaker?.label
      });

      // Update output device if changed
      if (defaultSpeaker && defaultSpeaker.deviceId !== this.selectedSpeakerId) {
        await this.setOutputDevice(defaultSpeaker.deviceId);
      }

      // Note: Changing input device mid-call requires more complex handling
      // as it may require renegotiating the WebRTC connection.
      // For now, we focus on output device which is the primary issue.
      if (defaultMic && defaultMic.deviceId !== this.selectedMicId) {
        console.warn("Microphone changed - may require page refresh for full effect");
        this.selectedMicId = defaultMic.deviceId;
      }
    } catch (error) {
      console.warn("Error handling device change:", error);
    }
  }

  handleDeviceError(error) {
    const { code, message } = error || {};
    console.error(`Twilio Client error${code ? ` (code ${code})` : ""}: ${message || "Unknown error"}`, error);

    if (code === 31204 || code === 31205) {
      console.error(
        "Twilio rejected the JWT. Double-check TWILIO_API_KEY, TWILIO_API_SECRET, and TWILIO_TWIML_APP_SID."
      );
    }

    // Handle microphone access errors
    if (code === 31402 || message?.includes("AcquisitionFailedError") || message?.includes("Could not start audio source")) {
      console.error("Microphone access failed. Please check:");
      console.error("1. Browser microphone permissions are granted");
      console.error("2. No other application is using the microphone");
      console.error("3. Microphone hardware is working");
      console.error("4. Try refreshing the page and granting permissions again");
      
      // Show user-friendly error message
      this.showMicrophoneError();
    }
  }

  showMicrophoneError() {
    // Create or update flash message
    const flashContainer = document.getElementById("flash");
    if (flashContainer) {
      flashContainer.innerHTML = `
        <div class="fixed top-4 right-4 z-50 p-4 max-w-md bg-red-50 rounded-lg border border-red-200 shadow-lg dark:bg-red-900/20 dark:border-red-800">
          <div class="flex items-start">
            <svg class="mt-0.5 mr-3 w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
            </svg>
            <div class="flex-1">
              <h3 class="text-sm font-semibold text-red-800 dark:text-red-200">Microphone Access Required</h3>
              <p class="mt-1 text-sm text-red-700 dark:text-red-300">
                Unable to access your microphone. Please:
              </p>
              <ul class="mt-2 space-y-1 text-sm list-disc list-inside text-red-600 dark:text-red-400">
                <li>Check browser microphone permissions</li>
                <li>Ensure no other app is using the microphone</li>
                <li>Refresh the page and try again</li>
              </ul>
            </div>
            <button data-action="click->twilio-client#dismissClosestAlert" class="ml-4 text-red-400 hover:text-red-600">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>
      `;
      
      // Auto-remove after 10 seconds
      setTimeout(() => {
        if (flashContainer) flashContainer.innerHTML = "";
      }, 10000);
    }
  }

  dismissClosestAlert(event) {
    event.preventDefault();
    event.currentTarget.closest("div")?.remove();
  }

  // Make an outbound call
  async makeCall(phoneNumber) {
    if (!this.device) {
      console.error("Twilio Device not initialized");
      return null;
    }

    if (!phoneNumber) {
      console.error("Phone number is required");
      return null;
    }

    // Check if there's already an active call
    if (this.currentCall || window.currentTwilioCall) {
      console.warn("A call is already active");
      this.dispatchCallEvent("error", {
        phoneNumber,
        error: { message: "A call is already active. Please end the current call first." }
      });
      return null;
    }

    try {
      // device.connect() returns a Promise that resolves to a Call object
      const call = await this.device.connect({
        params: {
          To: phoneNumber
        }
      });

      // Store active call reference
      this.currentCall = call;
      this.isMuted = false;
      window.currentTwilioCall = call;

      // Set up event listeners now that we have the actual Call object
      if (call && typeof call.on === "function") {
        // Store call SID when available - try multiple ways to get it
        let callSid = call.parameters?.CallSid || call.parameters?.callSid || call.parameters?.call_sid;
        
        // Also try to get it from the call object itself
        if (!callSid && call.outboundConnectionId) {
          callSid = call.outboundConnectionId;
        }
        if (!callSid && call.parameters?.OutboundConnectionId) {
          callSid = call.parameters.OutboundConnectionId;
        }
        
        call.on("accept", () => {
          // Get the call SID from the call object - try multiple sources
          callSid = call.parameters?.CallSid || call.parameters?.callSid || call.parameters?.call_sid ||
                    call.outboundConnectionId || call.parameters?.OutboundConnectionId || callSid;
          this.dispatchCallEvent("accepted", { phoneNumber, callSid });
        });

        call.on("disconnect", () => {
          this.currentCall = null;
          this.isMuted = false;
          window.currentTwilioCall = null;
          this.dispatchCallEvent("disconnected", { phoneNumber });
        });

        call.on("cancel", () => {
          this.currentCall = null;
          this.isMuted = false;
          window.currentTwilioCall = null;
          this.dispatchCallEvent("cancelled", { phoneNumber });
        });

        call.on("reject", () => {
          this.currentCall = null;
          this.isMuted = false;
          window.currentTwilioCall = null;
          this.dispatchCallEvent("rejected", { phoneNumber });
        });

        call.on("error", (error) => {
          console.error("Call error:", error);
          this.dispatchCallEvent("error", { phoneNumber, error });
        });

        call.on("ringing", () => {
          // Get the call SID when ringing
          callSid = call.parameters?.CallSid || call.parameters?.callSid || call.parameters?.call_sid || callSid;
          // Also try to get it from the call object itself
          if (!callSid && call.outboundConnectionId) {
            callSid = call.outboundConnectionId;
          }
          this.dispatchCallEvent("ringing", { phoneNumber, callSid });
        });
      }

      return call;
    } catch (error) {
      console.error("Failed to make call:", error);
      this.dispatchCallEvent("error", { phoneNumber, error });
      return null;
    }
  }

  // Hang up the current call
  hangUp() {
    if (this.currentCall) {
      try {
        if (typeof this.currentCall.disconnect === "function") {
          this.currentCall.disconnect();
        } else if (typeof this.currentCall.hangup === "function") {
          this.currentCall.hangup();
        } else {
          console.error("Call object does not have disconnect or hangup method");
        }
        this.currentCall = null;
        this.isMuted = false;
        window.currentTwilioCall = null;
      } catch (error) {
        console.error("Error hanging up call:", error);
      }
    } else if (window.currentTwilioCall) {
      // Fallback to window reference
      try {
        if (typeof window.currentTwilioCall.disconnect === "function") {
          window.currentTwilioCall.disconnect();
        } else if (typeof window.currentTwilioCall.hangup === "function") {
          window.currentTwilioCall.hangup();
        }
        window.currentTwilioCall = null;
        this.currentCall = null;
        this.isMuted = false;
      } catch (error) {
        console.error("Error hanging up call (fallback):", error);
      }
    }
  }

  // Mute/unmute the current call
  toggleMute() {
    if (this.currentCall) {
      try {
        // Track mute state ourselves since isMuted() doesn't exist
        this.isMuted = !this.isMuted;
        if (typeof this.currentCall.mute === "function") {
          this.currentCall.mute(this.isMuted);
        } else {
          console.error("Call object does not have mute method");
          this.isMuted = !this.isMuted; // Revert
          return false;
        }
        return this.isMuted;
      } catch (error) {
        console.error("Error toggling mute:", error);
        this.isMuted = !this.isMuted; // Revert on error
        return false;
      }
    } else if (window.currentTwilioCall) {
      // Fallback to window reference
      try {
        this.isMuted = !this.isMuted;
        if (typeof window.currentTwilioCall.mute === "function") {
          window.currentTwilioCall.mute(this.isMuted);
          return this.isMuted;
        }
        this.isMuted = !this.isMuted; // Revert
        return false;
      } catch (error) {
        console.error("Error toggling mute (fallback):", error);
        this.isMuted = !this.isMuted; // Revert on error
        return false;
      }
    }
    return false;
  }

  // Send DTMF digit
  sendDigit(digit) {
    const call = this.currentCall || window.currentTwilioCall;

    if (!call || typeof call.sendDigits !== "function") {
      console.warn("Twilio: Cannot send DTMF without an active call");
      return false;
    }

    try {
      call.sendDigits(digit);
      return true;
    } catch (error) {
      console.error("Twilio: Failed to send DTMF", error);
      return false;
    }
  }

  // Join a conference
  async joinConference(conferenceName) {
    if (!this.device) {
      console.error("Twilio Device not initialized");
      return null;
    }

    if (!conferenceName) {
      console.error("Conference name is required");
      return null;
    }

    // Check if there's already an active call
    if (this.currentCall || window.currentTwilioCall) {
      console.warn("A call is already active");
      this.dispatchCallEvent("error", {
        conferenceName,
        error: { message: "A call is already active. Please end the current call first." },
        isConference: true
      });
      return null;
    }

    try {
      // Resume AudioContext before joining conference (required for browser autoplay policy)
      this.resumeAudioContext();

      // Connect to the conference by calling a special format
      // The TwiML App will handle routing this to the conference
      const call = await this.device.connect({
        params: {
          To: conferenceName
        }
      });

      // Store active call reference
      this.currentCall = call;
      this.isMuted = false;
      window.currentTwilioCall = call;

      // Set up event listeners
      if (call && typeof call.on === "function") {
        call.on("accept", () => {
          this.resumeAudioContext(); // Resume again on accept
          this.dispatchCallEvent("accepted", { conferenceName, isConference: true });
        });

        call.on("disconnect", () => {
          this.currentCall = null;
          this.isMuted = false;
          window.currentTwilioCall = null;
          this.dispatchCallEvent("disconnected", { conferenceName, isConference: true });
        });

        call.on("error", (error) => {
          console.error("Conference error:", error);
          this.dispatchCallEvent("error", { conferenceName, error, isConference: true });
        });

        call.on("ringing", () => {
          this.dispatchCallEvent("ringing", { conferenceName, isConference: true });
        });
      }

      return call;
    } catch (error) {
      console.error("Error joining conference:", error);
      
      // Handle microphone access errors
      if (error.code === 31402 || error.message?.includes("AcquisitionFailedError") || error.message?.includes("Could not start audio source")) {
        this.showMicrophoneError();
      }
      
      this.dispatchCallEvent("error", { conferenceName, error, isConference: true });
      return null;
    }
  }

  // Set volume for the current call
  setVolume(volume) {
    // Volume should be between 0 and 1
    const clampedVolume = Math.max(0, Math.min(1, volume));
    
    if (this.currentCall) {
      try {
        // Twilio Device doesn't have a direct volume control
        // We need to adjust the audio element volume
        // The call object has an audio element we can access
        if (this.currentCall.localStream) {
          const audioTracks = this.currentCall.localStream.getAudioTracks();
          audioTracks.forEach(_track => {
            // Note: MediaStreamTrack doesn't have volume control
            // Volume is typically controlled at the HTMLAudioElement level
          });
        }
        
        // Try to find and adjust the remote audio element
        const remoteAudio = document.querySelector("audio[data-twilio-remote]");
        if (remoteAudio) {
          remoteAudio.volume = clampedVolume;
        }

        return true;
      } catch (error) {
        console.error("Error setting volume:", error);
        return false;
      }
    } else if (window.currentTwilioCall) {
      // Fallback
      try {
        const remoteAudio = document.querySelector("audio[data-twilio-remote]");
        if (remoteAudio) {
          remoteAudio.volume = clampedVolume;
        }
        return true;
      } catch (error) {
        console.error("Error setting volume (fallback):", error);
        return false;
      }
    }
    return false;
  }

  // Dispatch custom events
  dispatchCallEvent(eventName, detail = {}) {
    const event = new CustomEvent(`twilio:call:${eventName}`, {
      detail,
      bubbles: true
    });
    this.element.dispatchEvent(event);
  }
}
